import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { useStore } from "../stores/store";
import { Card, Row, Col, Badge, Button, Alert, Table } from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import TodayClassesPanel from './StaffPortal/SchedulePlan/TodayClassesPanel';
import './Home.css';

function Home() {
  const { mainStore } = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    mainStore.setMenuType(mainStore.HomeMenuType);
    mainStore.setMenuNo(mainStore.HomeMenuNo);
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/StaffPortalDashboard/GetSummary");
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error loading dashboard:", error);
      enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '.');
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    if (timeString.match(/^\d{2}:\d{2}$/)) return timeString;
    try {
      const time = new Date(`2000-01-01T${timeString}`);
      return time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return timeString;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="text-center py-5">
          <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} />
          <p className="mt-3 text-muted">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-container">
        <Alert variant="danger" className="mb-4">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {t("errorLoadingData")}
        </Alert>
      </div>
    );
  }

  const attendanceProgress = dashboardData.overallAttendanceRate || 0;
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (attendanceProgress / 100) * circumference;

  return (
    <div className="dashboard-container">
      {}
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h2 className="page-title">
              <i className="bi bi-speedometer2 me-2"></i>
              {t("dashboard")}
            </h2>
            <p className="page-subtitle">{t("welcomeBack")}, {dashboardData.firstName}!</p>
          </div>
          {dashboardData.currentWeekRange && (
            <div className="week-range-badge">
              <i className="bi bi-calendar-week me-2"></i>
              {dashboardData.currentWeekRange}
            </div>
          )}
        </div>
      </div>

      {}
      {dashboardData.alerts && dashboardData.alerts.length > 0 && (
        <div className="notifications-section mb-4">
          {dashboardData.alerts.map((alert, index) => (
            <Alert
              key={index}
              variant={alert.type === 'danger' ? 'danger' : alert.type === 'warning' ? 'warning' : 'info'}
              className="notification-alert mb-2"
            >
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center">
                  <i className={`${alert.icon} me-2 fs-5`}></i>
                  <div>
                    <strong>{alert.title}</strong>
                    <span className="ms-2 d-none d-md-inline">{alert.message}</span>
                  </div>
                </div>
                {alert.actionUrl && (
                  <Button
                    variant={alert.type === 'danger' ? 'danger' : alert.type === 'warning' ? 'warning' : 'outline-danger'}
                    size="sm"
                    onClick={() => navigate(alert.actionUrl)}
                  >
                    {alert.actionText}
                    <i className="bi bi-arrow-right ms-1"></i>
                  </Button>
                )}
              </div>
            </Alert>
          ))}
        </div>
      )}

      {/* Today's classes with their QR buttons; a lesson moved to another date takes no attendance. */}
      <TodayClassesPanel
        lessons={(dashboardData.thisWeekSchedule || []).filter(s => s.isToday && !s.isLessonReplaced)}
        attendanceWindow={dashboardData.attendanceWindow}
      />

      {}
      <Row xs={1} md={2} lg={4} className="g-4 mb-4">
        {}
        <Col>
          <Card className="dashboard-card stat-card h-100">
            <Card.Body>
              <h5 className="card-title mb-3">
                <i className="bi bi-calendar-check-fill me-2"></i>
                {t("todaysClasses")}
              </h5>
              <div className="text-center">
                <div className="stat-value-large text-danger">{dashboardData.classesToday || 0}</div>
                {dashboardData.nextClass && (
                  <div className="next-class-info mt-2">
                    <div className="text-muted small">{t("nextClass")}:</div>
                    <div className="fw-bold">{formatTime(dashboardData.nextClass.startTime)}</div>
                    <div className="small">{dashboardData.nextClass.roomCode}</div>
                    <div className="small text-truncate">{dashboardData.nextClass.subjectName}</div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {}
        <Col>
          <Card className="dashboard-card stat-card h-100">
            <Card.Body>
              <h5 className="card-title mb-3">
                <i className="bi bi-pencil-square me-2"></i>
                {t("pendingGrades")}
              </h5>
              <div className="text-center">
                <div className="stat-value-large text-danger">{dashboardData.pendingGradesCount || 0}</div>
                {dashboardData.isGradeDeadlineUrgent && dashboardData.pendingGradesCount > 0 && (
                  <Badge bg="danger" className="mt-2 pulse-badge">
                    {t("urgent")} - {dashboardData.daysUntilDeadline} {t("daysLeft")}
                  </Badge>
                )}
                {dashboardData.nearestGradeDeadline && !dashboardData.isGradeDeadlineUrgent && (
                  <div className="small text-muted mt-2">
                    {t("deadline")}: {formatDate(dashboardData.nearestGradeDeadline)}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {}
        <Col>
          <Card className="dashboard-card stat-card h-100">
            <Card.Body>
              <h5 className="card-title mb-3">
                <i className="bi bi-clipboard-data me-2"></i>
                {t("attendanceRate")}
              </h5>
              <div className="stat-card-content">
                <div className="circular-progress-wrapper">
                  <svg className="circular-progress" viewBox="0 0 100 100">
                    <circle className="circular-progress-bg" cx="50" cy="50" r="45" />
                    <circle
                      className="circular-progress-fill"
                      cx="50" cy="50" r="45"
                      style={{ strokeDasharray: circumference, strokeDashoffset: strokeDashoffset }}
                    />
                  </svg>
                  <div className="circular-progress-text">
                    <span className="progress-value">{dashboardData.overallAttendanceRate?.toFixed(0) || 0}</span>
                    <span className="progress-label">%</span>
                  </div>
                </div>
                <div className="attendance-meta mt-2">
                  <div className="meta-item justify-content-center small">
                    <span className="meta-value">
                      {dashboardData.totalSessionsConducted}/{dashboardData.totalScheduledSessions} {t("sessions")}
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {}
        <Col>
          <Card className="dashboard-card stat-card h-100">
            <Card.Body>
              <h5 className="card-title mb-3">
                <i className="bi bi-graph-up-arrow me-2"></i>
                {t("avgStudentGrade")}
              </h5>
              <div className="text-center">
                <div className="stat-value-large text-danger">
                  {dashboardData.averageStudentGrade?.toFixed(2) || "N/A"}
                </div>
                {dashboardData.passRate !== null && (
                  <Badge
                    bg={dashboardData.passRate >= 80 ? 'success' : dashboardData.passRate >= 60 ? 'warning' : 'secondary'}
                    className="mt-2"
                  >
                    {t("passRate")}: {dashboardData.passRate?.toFixed(1)}%
                  </Badge>
                )}
                {dashboardData.failedStudentsCount > 0 && (
                  <div className="small text-danger mt-1">
                    {dashboardData.failedStudentsCount} {t("failed")}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {}
      <Row xs={1} md={3} className="g-4 mb-4">
        {}
        <Col>
          <Card
            className="dashboard-card stat-card-clickable h-100"
            onClick={() => navigate('/ExamPlan/List')}
            style={{cursor: 'pointer'}}
          >
            <Card.Body className="d-flex align-items-center">
              <div className="stat-icon-box bg-danger-soft">
                <i className="bi bi-calendar-event text-danger"></i>
              </div>
              <div className="ms-3">
                <div className="stat-number">
                  {dashboardData.examDutiesCount || 0}
                  {dashboardData.upcomingExamDuties?.some(e => e.isToday || e.isUrgent) && (
                    <Badge bg="danger" className="ms-2 pulse-badge">{t("soon")}</Badge>
                  )}
                </div>
                <div className="stat-label">{t("examDuties")}</div>
              </div>
              <i className="bi bi-chevron-right ms-auto text-muted"></i>
            </Card.Body>
          </Card>
        </Col>

        {}
        <Col>
          <Card
            className="dashboard-card stat-card-clickable h-100"
            onClick={() => navigate('/Evaluation/List')}
            style={{cursor: 'pointer'}}
          >
            <Card.Body className="d-flex align-items-center">
              <div className="stat-icon-box bg-warning-soft">
                <i className="bi bi-star text-warning"></i>
              </div>
              <div className="ms-3">
                <div className="stat-number">{dashboardData.pendingEvaluationsCount || 0}</div>
                <div className="stat-label">{t("pendingEvaluations")}</div>
                {dashboardData.evaluationCompletionRate !== null && (
                  <div className="small text-muted">
                    {dashboardData.evaluationCompletionRate?.toFixed(0)}% {t("completed")}
                  </div>
                )}
              </div>
              <i className="bi bi-chevron-right ms-auto text-muted"></i>
            </Card.Body>
          </Card>
        </Col>

        {}
        <Col>
          <Card className="dashboard-card stat-card h-100">
            <Card.Body>
              <h5 className="card-title mb-2">
                <i className="bi bi-briefcase-fill me-2"></i>
                {t("workload")}
              </h5>
              <div className="workload-stats">
                <div className="workload-item">
                  <span className="workload-value">{dashboardData.totalCourses || 0}</span>
                  <span className="workload-label">{t("courses")}</span>
                </div>
                <div className="workload-item">
                  <span className="workload-value">{dashboardData.totalStudents || 0}</span>
                  <span className="workload-label">{t("students")}</span>
                </div>
                <div className="workload-item">
                  <span className="workload-value">{dashboardData.weeklyTeachingHours?.toFixed(1) || 0}</span>
                  <span className="workload-label">{t("hoursWeek")}</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {}
      {dashboardData.thisWeekSchedule && dashboardData.thisWeekSchedule.length > 0 && (
        <Card className="dashboard-card mb-4">
          <Card.Body>
            <h5 className="card-title mb-3">
              <i className="bi bi-calendar-week-fill me-2"></i>
              {t("thisWeekSchedule")}
              <Badge bg="secondary" className="ms-2">{dashboardData.thisWeekSchedule.length}</Badge>
            </h5>
            <div className="schedule-list">
              <Table responsive hover className="schedule-table mb-0">
                <thead>
                  <tr>
                    <th>{t("day")}</th>
                    <th>{t("time")}</th>
                    <th>{t("subject")}</th>
                    <th>{t("room")}</th>
                    <th>{t("group")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.thisWeekSchedule.slice(0, 10).map((schedule, index) => (
                    <tr
                      key={index}
                      className={`${schedule.isToday ? 'today-row' : ''} ${schedule.isPast ? 'past-row' : ''} ${schedule.isReplacement ? 'replacement-row' : ''}`}
                    >
                      <td>
                        <strong>{schedule.dayOfWeek}</strong>
                        <div className="small text-muted">{formatDate(schedule.date)}</div>
                        {schedule.isToday && <Badge bg="success" className="ms-2">Today</Badge>}
                      </td>
                      <td>
                        {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                      </td>
                      <td>
                        <div className="fw-bold">{schedule.subjectCode}</div>
                        <div className="small text-muted">{schedule.subjectName}</div>
                      </td>
                      <td>{schedule.roomCode || schedule.roomName}</td>
                      <td>{schedule.groupName}</td>
                      <td>
                        {schedule.isReplacement && (
                          <Badge bg="warning" text="dark">{t("replacement")}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {dashboardData.thisWeekSchedule.length > 10 && (
              <div className="text-center mt-3">
                <Button variant="link" className="text-danger" onClick={() => navigate('/SchedulePlan/List')}>
                  {t("viewFullSchedule")} <i className="bi bi-arrow-right"></i>
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {}
      {dashboardData.upcomingExamDuties && dashboardData.upcomingExamDuties.length > 0 && (
        <Card className="dashboard-card mb-4">
          <Card.Body>
            <h5 className="card-title mb-3">
              <i className="bi bi-calendar-event-fill me-2"></i>
              {t("upcomingExamDuties")}
              <Badge bg="secondary" className="ms-2">{dashboardData.upcomingExamDuties.length}</Badge>
            </h5>
            <div className="exam-list">
              {dashboardData.upcomingExamDuties.slice(0, 5).map((exam, index) => (
                <div key={index} className="exam-item">
                  <div className="exam-date-box">
                    <span className="exam-day">{new Date(exam.examDate).getDate()}</span>
                    <span className="exam-month">
                      {new Date(exam.examDate).toLocaleDateString('en', { month: 'short' })}
                    </span>
                  </div>
                  <div className="exam-info">
                    <h6 className="exam-subject">
                      <span className="subject-code">{exam.subjectCode}</span>
                      {exam.subjectName}
                    </h6>
                    <div className="exam-meta">
                      {exam.startTime && (
                        <span><i className="bi bi-clock-fill"></i>{formatTime(exam.startTime)} - {formatTime(exam.endTime)}</span>
                      )}
                      {exam.roomNames && (
                        <span><i className="bi bi-door-closed-fill"></i>{exam.roomNames}</span>
                      )}
                      {exam.branchName && (
                        <span><i className="bi bi-geo-alt-fill"></i>{exam.branchName}</span>
                      )}
                      {exam.estimatedStudents > 0 && (
                        <span><i className="bi bi-people-fill"></i>{exam.estimatedStudents} {t("students")}</span>
                      )}
                    </div>
                  </div>
                  <div className="exam-countdown">
                    <Badge
                      bg={exam.isToday ? 'danger' : exam.isUrgent ? 'warning' : 'secondary'}
                    >
                      {exam.daysUntilExam === 0 ? t("today") :
                       exam.daysUntilExam === 1 ? t("tomorrow") :
                       `${exam.daysUntilExam} ${t("days")}`}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {}
      <Row className="g-4">
        {}
        {dashboardData.coursesNeedingAttention && dashboardData.coursesNeedingAttention.length > 0 && (
          <Col lg={6}>
            <Card className="dashboard-card h-100">
              <Card.Body>
                <h5 className="card-title mb-3">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {t("coursesNeedingAttention")}
                </h5>
                <div className="attention-courses-list">
                  {dashboardData.coursesNeedingAttention.map((course, index) => (
                    <div key={index} className={`attention-course-item severity-${course.severityLevel}`}>
                      <div className="course-header">
                        <strong>{course.subjectCode}</strong> - {course.subjectName}
                        {course.groupName && <span className="text-muted ms-2">({course.groupName})</span>}
                      </div>
                      <div className="course-metrics">
                        {course.attendanceRate !== null && (
                          <span className={course.attendanceRate < 60 ? 'text-danger' : 'text-warning'}>
                            <i className="bi bi-person-check"></i> {course.attendanceRate.toFixed(1)}% {t("attendance")}
                          </span>
                        )}
                        {course.pendingGrades > 0 && (
                          <span className="text-warning">
                            <i className="bi bi-pencil"></i> {course.pendingGrades} {t("pending")}
                          </span>
                        )}
                        {course.averageGrade !== null && (
                          <span className={course.averageGrade < 6.5 ? 'text-danger' : ''}>
                            <i className="bi bi-graph-up"></i> {course.averageGrade.toFixed(2)} {t("avg")}
                          </span>
                        )}
                        {course.failedStudents > 0 && (
                          <span className="text-danger">
                            <i className="bi bi-x-circle"></i> {course.failedStudents} {t("failed")}
                          </span>
                        )}
                      </div>
                      <div className="course-issues">
                        {course.issues.map((issue, i) => (
                          <Badge key={i} bg="warning" text="dark" className="me-1">{issue}</Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {}
        <Col lg={dashboardData.coursesNeedingAttention && dashboardData.coursesNeedingAttention.length > 0 ? 6 : 12}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {t("recentGradeSubmissions")}
                </h5>
                <Button
                  variant="link"
                  className="text-danger p-0"
                  onClick={() => navigate('/ExamRegistration/GradeManagement')}
                >
                  {t("viewAll")} <i className="bi bi-arrow-right"></i>
                </Button>
              </div>
              {dashboardData.recentGradeSubmissions && dashboardData.recentGradeSubmissions.length > 0 ? (
                <div className="grades-list">
                  {dashboardData.recentGradeSubmissions.map((grade, index) => (
                    <div key={index} className="grade-item">
                      <div className="grade-subject-info">
                        <span className="grade-code">{grade.subjectCode}</span>
                        <span className="grade-name">{grade.subjectName}</span>
                        {grade.termName && <span className="text-muted small">({grade.termName})</span>}
                      </div>
                      <div className="grade-details">
                        <span className="grade-stats small">
                          {grade.studentsGraded} {t("studentsGraded")} | Avg: {grade.averageGrade?.toFixed(2)}
                        </span>
                        <div className="grade-date small text-muted">
                          {formatDate(grade.submissionDate)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <i className="bi bi-inbox"></i>
                  <p>{t("noRecentGrades")}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Home;
